import React from 'react';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, ArrowRight, Clock, Users, Zap, Shield, Database, Code, Workflow, FileText } from 'lucide-react';

interface ERPDetailsModalProps {
  erp: {
    name: string;
    logo: string;
    description: string;
    capabilities: string[];
    methodology: string;
    badge: string;
    badgeColor: string;
  };
  children: React.ReactNode;
}

export const ERPDetailsModal = ({ erp, children }: ERPDetailsModalProps) => {
  // ERP-specific detailed content
  const getERPDetails = (erpName: string) => {
    switch (erpName) {
      case "Oracle EBS":
        return {
          technicalSpecs: [
            {
              icon: <Database className="w-5 h-5 text-primary" />,
              title: "Advanced 3-Way Matching Engine",
              description: "Automated PO-Receipt-Invoice matching with configurable tolerance levels. Handles blanket purchase orders, release-based matching, and complex receipt scenarios across multiple locations."
            },
            {
              icon: <Shield className="w-5 h-5 text-primary" />,
              title: "Duplicate Invoice Detection",
              description: "Real-time duplicate checking across invoice number, supplier, amount, and date combinations. Integrates with EBS supplier master and payment history for comprehensive duplicate prevention."
            },
            {
              icon: <Code className="w-5 h-5 text-primary" />,
              title: "Supplier Master Synchronization",
              description: "Bi-directional sync with EBS supplier records, including site-specific data, payment terms, and banking information. Automatic supplier validation and new vendor request workflows."
            },
            {
              icon: <Workflow className="w-5 h-5 text-primary" />,
              title: "Blanket PO & Release Management",
              description: "Intelligent processing of blanket purchase orders with automatic release creation, quantity tracking, and budget consumption monitoring across multi-year agreements."
            }
          ],
          implementationDetails: [
            "Configure XML Gateway for bi-directional data exchange",
            "Set up custom invoice import concurrent program",
            "Establish secure database links and user accounts",
            "Configure responsibility and menu structures",
            "Map chart of accounts and flex field structures",
            "Set up automated email notifications through EBS"
          ],
          useCases: [
            {
              title: "Multi-Org Manufacturing",
              description: "Large manufacturer with 12 operating units uses automated GL coding by org, project, and cost center with 99.2% accuracy."
            },
            {
              title: "Complex 3-Way Matching",
              description: "Automates PO-Receipt-Invoice matching across multiple receiving locations with exception handling for quantity and price variances."
            }
          ],
          uniqueBenefits: [
            "Leverages existing EBS security model and user roles",
            "Maintains complete audit trail within EBS tables",
            "Supports complex multi-currency and multi-org scenarios",
            "Integrates with EBS supplier management and purchasing modules"
          ]
        };

      case "Oracle Cloud ERP":
        return {
          technicalSpecs: [
            {
              icon: <Database className="w-5 h-5 text-primary" />,
              title: "Intelligent 2-Way & 3-Way Matching",
              description: "Cloud-native matching engine with ML-powered exception handling. Supports blanket PO releases, contract-based matching, and automated tolerance management with real-time budget validation."
            },
            {
              icon: <Shield className="w-5 h-5 text-primary" />,
              title: "Advanced Duplicate Prevention",
              description: "AI-powered duplicate detection across supplier, amount, and invoice patterns. Integration with Oracle Cloud Supplier Portal for real-time validation and automated vendor communication."
            },
            {
              icon: <Code className="w-5 h-5 text-primary" />,
              title: "Cloud Supplier Management",
              description: "Unified supplier master with automatic onboarding workflows, risk assessment integration, and spend analytics. Real-time supplier performance tracking and automated compliance monitoring."
            },
            {
              icon: <Workflow className="w-5 h-5 text-primary" />,
              title: "Blanket PO Optimization",
              description: "Smart blanket purchase order processing with predictive release scheduling, budget consumption alerts, and automated contract renewal workflows for enterprise agreements."
            }
          ],
          implementationDetails: [
            "Configure OAuth application in Oracle Identity Cloud",
            "Set up REST service endpoints and authentication",
            "Map business units and legal entities",
            "Configure approval workflow integration",
            "Establish real-time data synchronization",
            "Set up Cloud monitoring and alerting"
          ],
          useCases: [
            {
              title: "Global Services Company",
              description: "Processes invoices across 25 countries with automated currency conversion, tax calculation, and compliance reporting."
            },
            {
              title: "Project-Based Organization",
              description: "Automatic project and task assignment for professional services invoices with real-time budget tracking and alerts."
            }
          ],
          uniqueBenefits: [
            "Cloud-native scalability and 99.9% uptime SLA",
            "Real-time analytics and reporting through Oracle Analytics Cloud",
            "Automatic updates with new Oracle Cloud features",
            "Integration with Oracle HCM and SCM Cloud modules"
          ]
        };

      case "Yardi Voyager":
        return {
          technicalSpecs: [
            {
              icon: <Database className="w-5 h-5 text-primary" />,
              title: "Property-Focused 3-Way Matching",
              description: "Specialized matching for property management workflows including work orders, service contracts, and blanket maintenance agreements. Handles property-specific receiving and multi-location deliveries."
            },
            {
              icon: <Shield className="w-5 h-5 text-primary" />,
              title: "Vendor & Duplicate Management",
              description: "Integrated duplicate detection with Yardi vendor master and insurance certificate tracking. Prevents duplicate payments across properties and automatically validates contractor credentials."
            },
            {
              icon: <FileText className="w-5 h-5 text-primary" />,
              title: "Supplier Compliance Tracking",
              description: "Automated supplier onboarding with insurance certificate validation, license verification, and compliance documentation. Real-time alerts for expiring certificates and renewals."
            },
            {
              icon: <Workflow className="w-5 h-5 text-primary" />,
              title: "Blanket Service Agreements",
              description: "Intelligent processing of blanket service contracts for maintenance, utilities, and property services. Automated budget tracking and spend allocation across property portfolios."
            }
          ],
          implementationDetails: [
            "Configure Yardi database integration and stored procedures",
            "Set up property hierarchy and GL account mapping",
            "Establish vendor and insurance certificate sync",
            "Configure property-specific approval workflows",
            "Map expense categories and cost recovery rules",
            "Set up automated tenant billing integration"
          ],
          useCases: [
            {
              title: "Housing Authority Portfolio",
              description: "Manages 2,500+ units across multiple properties with automated compliance reporting and HUD grant tracking."
            },
            {
              title: "Commercial Real Estate REIT",
              description: "Processes maintenance and capital improvement invoices with automatic property allocation and tenant cost recovery."
            }
          ],
          uniqueBenefits: [
            "Specialized for property management workflows and compliance",
            "Automated insurance certificate and license tracking",
            "Integration with property maintenance and work order systems",
            "Support for HUD, tax credit, and grant funding requirements"
          ]
        };

      default:
        return {
          technicalSpecs: [],
          implementationDetails: [],
          useCases: [],
          uniqueBenefits: []
        };
    }
  };

  const details = getERPDetails(erp.name);

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={erp.logo} alt={erp.name} className="h-12 w-auto" />
              <div>
                <DialogTitle className="text-2xl">{erp.name} Integration</DialogTitle>
                <DialogDescription>
                  Complete integration details and capabilities
                </DialogDescription>
              </div>
            </div>
            <Badge className={`${erp.badgeColor} text-sm px-3 py-1`}>
              {erp.badge}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Overview */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Overview</h3>
            <p className="text-muted-foreground">{erp.description}</p>
            
            <div className="flex items-center gap-6 mt-4 text-sm">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-1 text-primary" />
                {erp.methodology}
              </div>
            </div>
          </div>

          {/* Core Capabilities */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Core Capabilities</h3>
            <div className="grid gap-3">
              {erp.capabilities.map((capability, index) => (
                <div key={index} className="flex items-center text-sm">
                  <CheckCircle className="w-4 h-4 text-accent mr-3 flex-shrink-0" />
                  {capability}
                </div>
              ))}
            </div>
          </div>

          {/* Technical Architecture */}
          {details.technicalSpecs.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Technical Architecture</h3>
              <div className="space-y-4">
                {details.technicalSpecs.map((spec, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-muted/50 rounded-lg">
                    <div className="flex-shrink-0 p-2 bg-primary/10 rounded-lg">
                      {spec.icon}
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">{spec.title}</h4>
                      <p className="text-sm text-muted-foreground">{spec.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* Implementation Process */}
          {details.implementationDetails.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Implementation Process</h3>
              <div className="space-y-2">
                {details.implementationDetails.map((step, index) => (
                  <div key={index} className="flex items-start text-sm">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                      {index + 1}
                    </div>
                    {step}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* Real-World Use Cases */}
          {details.useCases.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Real-World Use Cases</h3>
              <div className="space-y-4">
                {details.useCases.map((useCase, index) => (
                  <div key={index} className="p-4 border rounded-lg">
                    <h4 className="font-medium text-primary mb-2">{useCase.title}</h4>
                    <p className="text-sm text-muted-foreground">{useCase.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* Unique Benefits */}
          {details.uniqueBenefits.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Why Choose EZ Cloud for {erp.name}?</h3>
              <div className="space-y-3">
                {details.uniqueBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                    {benefit}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="pt-4 border-t">
            <Button 
              className="w-full" 
              onClick={() => openCalendlyDemo()}
            >
              Schedule {erp.name} Integration Demo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};